/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable max-statements, max-depth, complexity */
import { AuthSdkError } from '../errors';
import { Remediator } from './remediators';
import { isIdxResponse, } from './types/idx-js';
// Return first match idxRemediation in allowed remediators
export function getRemediator(idxRemediations, values, options) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const remediators = options.remediators;
    let remediator;
    // remediation name specified by caller - fast-track remediator lookup 
    if (options.step) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const remediation = idxRemediations.find(({ name }) => name === options.step);
        const T = remediators[remediation.name];
        return new T(remediation, values);
    }
    const remediatorCandidates = [];
    for (let remediation of idxRemediations) {
        const isRemeditionInFlow = Object.keys(remediators).includes(remediation.name);
        if (!isRemeditionInFlow) {
            continue;
        }
        const T = remediators[remediation.name];
        remediator = new T(remediation, values);
        if (remediator.canRemediate()) {
            // found the remediator
            return remediator;
        }
        // remediator cannot handle the current values
        // maybe return for next step
        remediatorCandidates.push(remediator);
    }
    return remediatorCandidates[0];
}
function isTerminalResponse(idxResponse) {
    const { neededToProceed, interactionCode } = idxResponse;
    return !neededToProceed.length && !interactionCode;
}
function canSkipFn(idxResponse) {
    return idxResponse.neededToProceed.some(({ name }) => name === 'skip');
}
function canResendFn(idxResponse) {
    return Object.keys(idxResponse.actions).some(actionName => actionName.includes('resend'));
}
function getIdxMessages(idxResponse) {
    var _a;
    let messages = [];
    const { rawIdxState, neededToProceed } = idxResponse;
    // Handle global messages
    const globalMessages = (_a = rawIdxState.messages) === null || _a === void 0 ? void 0 : _a.value.map(message => message);
    if (globalMessages) {
        messages = [...messages, ...globalMessages];
    }
    // Handle field messages for current flow
    for (let remediation of neededToProceed) {
        const fieldMessages = Remediator.getMessages(remediation);
        if (fieldMessages) {
            messages = [...messages, ...fieldMessages];
        }
    }
    return messages;
}
function getNextStep(remediator, idxResponse) {
    const nextStep = remediator.getNextStep(idxResponse.context);
    const canSkip = canSkipFn(idxResponse);
    const canResend = canResendFn(idxResponse);
    return Object.assign(Object.assign(Object.assign({}, nextStep), (canSkip && { canSkip })), (canResend && { canResend }));
}
function handleIdxError(e, remediator) {
    // Handle idx messages
    const idxState = isIdxResponse(e) ? e : null;
    if (!idxState) {
        // Thrown error terminates the interaction with idx
        throw e;
    }
    const terminal = isTerminalResponse(idxState);
    const messages = getIdxMessages(idxState);
    if (terminal) {
        return { terminal, messages };
    }
    else {
        const nextStep = remediator && getNextStep(remediator, idxState);
        return Object.assign({ messages }, (nextStep && { nextStep }));
    }
}
function getActionFromValues(values, idxResponse) {
    // Currently support resend actions only
    return Object.keys(idxResponse.actions).find(action => !!values.resend && action.includes('-resend'));
}
function removeActionFromValues(values) {
    // Currently support resend actions only
    values.resend = undefined;
    return values;
}
// This function is called recursively until it reaches success or cannot be remediated
export function remediate(idxResponse, values, options) {
    return __awaiter(this, void 0, void 0, function* () {
        let { neededToProceed, interactionCode } = idxResponse;
        const { remediators, flow } = options;
        // If the response contains an interaction code, there is no need to remediate
        if (interactionCode) {
            return { idxResponse };
        }
        // Reach to terminal state
        const terminal = isTerminalResponse(idxResponse);
        const messages = getIdxMessages(idxResponse);
        if (terminal) {
            return { terminal, messages };
        }
        const remediator = getRemediator(neededToProceed, values, options);
        if (!remediator && flow === 'default') {
            return { idxResponse };
        }
        if (!remediator) {
            throw new AuthSdkError(`
      No remediation can match current flow, check policy settings in your org.
      Remediations: [${neededToProceed.reduce((acc, curr) => acc ? acc + ' ,' + curr.name : curr.name, '')}]
    `);
        }
        if (messages.length) {
            const nextStep = getNextStep(remediator, idxResponse);
            return { nextStep, messages };
        }
        // Try actions in idxResponse first
        const actionFromValues = getActionFromValues(values, idxResponse);
        const actions = [
            ...options.actions || [],
            ...(actionFromValues && [actionFromValues] || []),
        ];
        if (actions) {
            for (let action of actions) {
                let valuesWithoutExecutedAction = removeActionFromValues(values);
                if (typeof idxResponse.actions[action] === 'function') {
                    try {
                        idxResponse = yield idxResponse.actions[action]();
                    }
                    catch (e) {
                        return handleIdxError(e, remediators);
                    }
                    if (action === 'cancel') {
                        return { canceled: true };
                    }
                    return remediate(idxResponse, valuesWithoutExecutedAction, options); // recursive call
                }
            }
        }
        // Return next step to the caller
        if (!remediator.canRemediate()) {
            const nextStep = getNextStep(remediator, idxResponse);
            return { idxResponse, nextStep };
        }
        const name = remediator.getName();
        const data = remediator.getData();
        try {
            idxResponse = yield idxResponse.proceed(name, data);
            // We may want to trim the values bag for the next remediation
            // Let the remediator decide what the values should be (default to current values)
            values = remediator.getValuesAfterProceed();
            return remediate(idxResponse, values, options); // recursive call
        }
        catch (e) {
            return handleIdxError(e, remediator);
        }
    });
}
